import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import LockIcon from '@mui/icons-material/Lock';
import { Box, Stack, Switch, Tooltip, Typography } from '@mui/material';

import { themeColors } from 'colors';
import Chip from 'domains/core/components/Chip';
import { AccessCentrePermissionKey } from 'models/enums';

import type { AccessCentrePermission } from 'domains/accessCentre/types';

const AUTO_ENABLED_LABEL = 'auto-enabled';

/** `contacts.view_phi` — highlighted row + red switch; labels On/Off. */
export const isContactsViewPhiPermissionRow = (permission: AccessCentrePermission): boolean =>
    permission.key === AccessCentrePermissionKey.ContactsViewPhi;

const autoEnabledChipSx = {
    height: 'auto',
    flexShrink: 0,
    borderColor: themeColors.roleBuilder.autoEnabledTagBorder,
    color: themeColors.roleBuilder.autoEnabledTagText,
    backgroundColor: 'transparent',
    '& .MuiChip-label': {
        fontSize: '0.75rem',
        lineHeight: 1.2,
        px: 0.75,
        py: 0.25,
    },
} as const;

/** Below `md`, stack into one column so toggles stay in-flow (viewport `sm` can still be wide while the panel is narrow). From `md`, row layout; `overflowX` catches narrow side-by-side panels. Third column groups chip + toggle so spacing can stay tight. */
const rowGridSx = {
    display: 'grid',
    gridTemplateColumns: {
        xs: '1fr',
        md: 'minmax(140px, 2fr) minmax(120px, 2fr) auto',
    },
    alignItems: 'center',
    gap: 2,
    py: 1,
    px: 1.5,
    minWidth: 0,
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
} as const;

/** No horizontal margin: parent permission panel uses `overflow: hidden`, which clips side margins and hid the tint. */
const phiPermissionRowHighlightSx = {
    bgcolor: themeColors.roleBuilder.viewPhiPermissionRowBackground,
    borderRadius: 1,
    m: 1,
    p: 1.5,
} as const;

const permissionRowGridWithPhiHighlightSx = {
    ...rowGridSx,
    ...phiPermissionRowHighlightSx,
} as const;

/** body2 at 16px */
const permissionNameSx = {
    fontSize: '16px',
    fontWeight: 400,
} as const;

const permissionDescriptionSx = {
    fontSize: '13px',
    minWidth: 0,
} as const;

/** Tighter label column for On/Off. */
const toggleLabelCompactSx = {
    fontSize: '13px',
    minWidth: 28,
    textAlign: 'right' as const,
} as const;

const permissionNameCellStackSx = { minWidth: 0 } as const;

const dependencyTooltipContentSx = {
    maxWidth: 320,
    color: '#ffffff',
    '& .MuiTypography-root': {
        color: '#ffffff',
        fontSize: '0.8125rem',
        lineHeight: 1.25,
    },
} as const;

const dependencyTooltipIntroTypographySx = {
    m: 0,
    mb: 0.25,
    lineHeight: 1.25,
} as const;

const dependencyTooltipLineTypographySx = {
    m: 0,
    mt: 0,
    lineHeight: 1.2,
} as const;

/** Same green as global tooltips (`themeColors.action.active`); text forced white for rich title content. */
const dependencyTooltipSurfaceSx = {
    bgcolor: themeColors.action.active,
    color: '#ffffff',
    maxWidth: 360,
} as const;

const dependencyInfoIconWrapSx = {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
} as const;

const lockIconSx = {
    fontSize: 20,
    color: 'text.secondary',
    flexShrink: 0,
} as const;

const gppMaybeOutlinedIconSx = {
    fontSize: 18,
    color: 'text.secondary',
} as const;

const autoEnabledTagPlaceholderBoxSx = { minWidth: 88 } as const;

/** Tight gap between auto-enabled chip and switch (same grid cell; avoids wide `gap` between former columns). */
const chipToggleClusterOuterSx = { flexWrap: 'wrap', minWidth: 0 } as const;

const DEPENDENCY_TOOLTIP_INTRO = 'Enabling this will automatically enable:';

export type PermissionRowProps = {
    permission: AccessCentrePermission;
    /** Sorted display names of permissions that turn on when this one is enabled (transitive). */
    autoEnabledDependencyNames: string[];
    isOn: boolean;
    showAutoEnabledTag: boolean;
    onToggle: (id: number) => void;
    readOnly: boolean;
};

const PermissionRow = ({
    permission,
    autoEnabledDependencyNames,
    isOn,
    showAutoEnabledTag,
    onToggle,
    readOnly,
}: PermissionRowProps) => {
    const isPhiRow = isContactsViewPhiPermissionRow(permission);
    const toggleLabel = isOn ? 'On' : 'Off';

    return (
        <Box
            sx={isPhiRow ? permissionRowGridWithPhiHighlightSx : rowGridSx}
            data-testid={`access-center-permission-row-${permission.id}`}
        >
            <Stack direction="row" alignItems="center" spacing={0.75} sx={permissionNameCellStackSx}>
                {isPhiRow ? <LockIcon sx={lockIconSx} aria-hidden /> : null}
                <Typography variant="body2" sx={permissionNameSx}>
                    {permission.name}
                </Typography>
                {autoEnabledDependencyNames.length > 0 ? (
                    <Tooltip
                        title={
                            <Box component="div" sx={dependencyTooltipContentSx}>
                                <Typography variant="body2" component="p" sx={dependencyTooltipIntroTypographySx}>
                                    {DEPENDENCY_TOOLTIP_INTRO}
                                </Typography>
                                <Box
                                    component="ul"
                                    sx={{
                                        m: 0,
                                        pl: 2.25,
                                        listStyleType: 'disc',
                                    }}
                                >
                                    {autoEnabledDependencyNames.map((name, index) => (
                                        <Typography
                                            key={`${name}-${index}`}
                                            variant="body2"
                                            component="li"
                                            sx={dependencyTooltipLineTypographySx}
                                        >
                                            {name}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                        }
                        componentsProps={{
                            tooltip: {
                                sx: dependencyTooltipSurfaceSx,
                            },
                            popper: {
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: { offset: [0, -8] },
                                    },
                                ],
                            },
                        }}
                    >
                        <Box
                            component="span"
                            sx={dependencyInfoIconWrapSx}
                            aria-label="Dependency information"
                            data-testid={`access-center-permission-dependency-info-${permission.id}`}
                        >
                            <GppMaybeOutlinedIcon sx={gppMaybeOutlinedIconSx} />
                        </Box>
                    </Tooltip>
                ) : null}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={permissionDescriptionSx}>
                {permission.description ?? '—'}
            </Typography>
            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={0.5}
                sx={chipToggleClusterOuterSx}
            >
                {showAutoEnabledTag && isOn ? (
                    <Chip
                        label={AUTO_ENABLED_LABEL}
                        variant="outlined"
                        size="small"
                        sx={autoEnabledChipSx}
                        data-testid={`access-center-permission-auto-enabled-${permission.id}`}
                    />
                ) : (
                    <Box sx={autoEnabledTagPlaceholderBoxSx} />
                )}
                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={0.25}>
                    <Switch
                        color={isPhiRow ? 'default' : 'secondary'}
                        size="small"
                        checked={isOn}
                        onChange={() => onToggle(permission.id)}
                        disabled={readOnly}
                        inputProps={{
                            'aria-label': `${permission.name} permission`,
                        }}
                        data-testid={`access-center-permission-toggle-${permission.id}`}
                        sx={
                            isPhiRow
                                ? {
                                      '& .MuiSwitch-switchBase.Mui-checked': {
                                          color: themeColors.error.main,
                                          '&:hover': {
                                              backgroundColor: themeColors.error.outlinedHoverBackground,
                                          },
                                      },
                                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                          backgroundColor: themeColors.error.main,
                                      },
                                  }
                                : undefined
                        }
                    />
                    <Typography variant="body2" color="text.secondary" sx={toggleLabelCompactSx} component="span">
                        {toggleLabel}
                    </Typography>
                </Stack>
            </Stack>
        </Box>
    );
};

export default PermissionRow;
